import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { Logger } from '@app/shared/utils/logger.utils';
import { BehaviorSubject, fromEvent, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private _window: Window;
  private destroy$ = new Subject<void>();
  private _appVersion$ = new Subject<string>();
  private _showHelpModal$ = new Subject<boolean>();

  private loader$ = new BehaviorSubject(false);
  private onlineStatus$ = new BehaviorSubject(navigator.onLine);
  private offlineStatus$ = new BehaviorSubject(!navigator.onLine);

  online$ = this.onlineStatus$.asObservable();
  offline$ = this.offlineStatus$.asObservable();
  appVersion$ = this._appVersion$.asObservable();

  get window() {
    return this._window;
  }

  constructor(@Inject(DOCUMENT) private document: Document, updates: SwUpdate) {
    this._window = this.document.defaultView as Window;

    fromEvent(this._window, 'online')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onlineStatus$.next(true);
        this.offlineStatus$.next(false);
      });

    fromEvent(this._window, 'offline')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onlineStatus$.next(false);
        this.offlineStatus$.next(true);
      });

    updates.versionUpdates.pipe(takeUntil(this.destroy$)).subscribe((evt: VersionEvent) => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          Logger.info(`Downloading new app version: ${evt.version.hash}`);
          break;
        case 'VERSION_READY':
          Logger.info(`Current app version: ${evt.currentVersion.hash}`);
          Logger.info(`New app version ready for use: ${evt.latestVersion.hash}`);
          if (evt.currentVersion.hash !== evt.latestVersion.hash) {
            this._appVersion$.next(evt.currentVersion.hash);
          }
          break;
        case 'VERSION_INSTALLATION_FAILED':
          Logger.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });
  }

  getLoaderStatus() {
    return this.loader$.getValue();
  }

  showLoader() {
    this.loader$.next(true);
  }

  hideLoader() {
    this.loader$.next(false);
  }

  destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadApp() {
    document.location.reload();
  }

  showHelpModal() {
    this._showHelpModal$.next(true);
  }

  hideHelpModal() {
    this._showHelpModal$.next(false);
    if (window.Lighthouse) {
      window.Lighthouse.hideButton();
      window.Lighthouse.hide();
    }
  }

  lightHouseWidgetNavigate(page: 'category' | 'article', pageId: string) {
    this._showHelpModal$.next(true);
    if (window.Lighthouse) {
      window.Lighthouse.showButton();
      window.Lighthouse.show();
      window.Lighthouse.navigate(page, pageId);
    }
  }

  get onHelpModalVisibleChange$() {
    return this._showHelpModal$.asObservable();
  }
}
